import { ArrowLeft, Check, Pen, X } from 'lucide-react'
import styles from './CompliancesList.module.css'
import Table from '@/features/erp/components/table/Table'
import type { GetCompliancesOutput } from '@montatec/contracts'
import Status from '../../../status/Status'
import Chip from '../../../chip/Chip'
import { useState } from 'react'
import { useCompliancesMutations } from '@/features/erp/modules/providers/hooks/compliances/useProviders'

type Props = {
    data?: GetCompliancesOutput
    onGoToProviders: (provider: string) => void
}

const dateToSpanish = (isoString: string | Date, tipo: 'corto' | 'largo' = 'largo') => {
    if (!isoString) return '';
    const date = new Date(isoString);
    
    return date.toLocaleDateString('es-ES', {
        day: tipo === 'corto' ? '2-digit' : 'numeric',
        month: tipo === 'corto' ? '2-digit' : 'long',
        year: 'numeric',
        timeZone: 'UTC'
    });
};

export default function CompliancesList({data, onGoToProviders} : Props) {

    const [input, setInput] = useState("")
    const [isOpenInputId, setOpenInputId] = useState<number | null>(null)

    const {updateCompliance} = useCompliancesMutations()

    const handleOpenInput = (id: number, date: string | null | undefined) => {
        if(date) {            
            setInput(date.split('T')[0])            
        }

        setOpenInputId(id)
    }

    const handleCloseInput = () => {        
        setOpenInputId(null)
        setInput("")
    }

    const handleSave = (id: number, actualDate: string | null | undefined) => {        
        handleCloseInput()
        
        if(actualDate && new Date(actualDate).getTime() == new Date(input).getTime()) {
            return            
        } 
        
        updateCompliance.mutate({
            id: id,
            data: {
                issuedAt: new Date(input)
            }
        })
    }

    const calculateDate = (days: number) => {
        const actualDate = new Date()        
  
        // Usamos los métodos UTC para evitar desfases horarios locales
        actualDate.setUTCDate(actualDate.getUTCDate() + days);
        
        return dateToSpanish(actualDate);
    }

    return (
        <Table
            headers={[                
                { id: 1, col: "Nombre del documento"        },
                { id: 2, col: "Proveedor asociado"          }, 
                { id: 3, col: "Ultima fecha de emisión", },                
                { id: 4, col: "Fecha de vencimiento",       },                
                { id: 5, col: "Dias restantes"              },                                                
                { id: 6, col: "Estado",                     },
            ]}

            body={ data?.map((compliance) => ([
                { id: 1, col: <Chip>{compliance.document}</Chip> },
                
                { id: 2, col:
                    <Chip onClick={() => onGoToProviders(compliance.provider)} className={styles.provider} hoverColor>
                        <ArrowLeft width={1} height={1} className={`${styles.compliant}`}/>
                        <span>{compliance.provider}</span>
                    </Chip>
                },
                
                { id: 3, col: 
                    <Chip className={`${styles.issued}`} onClick={() => handleOpenInput(compliance.id, compliance.issuedAt)} hoverColor={isOpenInputId !== compliance.id}>
                        { isOpenInputId !== compliance.id ?
                            <>
                                <span>{compliance.issuedAt ? dateToSpanish(compliance.issuedAt, "largo") : "___ ___ _____"}</span>
                                <Pen width={12} height={12}/>
                            </>
                        :
                            <>
                                <input value={input}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}                                    
                                    autoFocus type="date"
                                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                        if(e.code === "Enter") handleSave(compliance.id, compliance.issuedAt)}
                                    }                                     
                                />
                                <X className={styles.red} width={12} height={12} 
                                    onClick={(e) => {e.stopPropagation(); handleCloseInput()}}
                                />
                                <Check width={12} height={12}
                                    onClick={(e) => {e.stopPropagation(); handleSave(compliance.id, compliance.issuedAt)}}
                                />                        
                            </>
                        }
                    </Chip>                                                            
                },
                
                { id: 4, col: 
                    <span> {
                        compliance.expireAt ? compliance.expireAt < 0 ? 
                                `Vencio el ${calculateDate(compliance.expireAt)}` 
                            : 
                                `Vence el ${calculateDate(compliance.expireAt)}` 
                        : "Indefinida"
                    }</span> 
                },
                
                { id: 5, col: 
                    <span> {
                        compliance.expireAt ? compliance.expireAt < 0 ? 
                                `Vencio hace ${Math.abs(compliance.expireAt)} días` 
                            : 
                                `Vence en ${Math.abs(compliance.expireAt)} días` 
                        : "Indefinido"
                    }</span> 
                },
                
                { id: 6, col: <Status type={'compliance'} status={compliance.status} />},
            ])) ?? []}

            align={[
                [1, 'left'],
                [2, 'left'],
                [3, 'center'],
                [4, 'center'],       
                [5, 'center'],             
            ]}
        />
    )
}
